import { toast } from "@/shared/ui/toast";
import { api, apiBaseUrl } from "./client";
import { errorMessage } from "./error-messages";

/** Soft-delete a file by name inside a folder (requires EDIT on the folder). */
export async function deleteFileByName(folderId: string, name: string): Promise<void> {
  await api.DELETE("/files/by-name", { params: { query: { folder_id: folderId, name } } });
}

export type SignedUrlKind = "download" | "preview";

/** Signed URLs are single-use: fetch right before use, never store, never log. */
export async function fetchSignedUrl(
  kind: SignedUrlKind,
  fileId: string,
): Promise<{ url: string; expires_in: number }> {
  const path =
    kind === "download" ? "/files/{file_id}/download-url" : "/files/{file_id}/preview-url";
  const { data } = await api.GET(path, { params: { path: { file_id: fileId } } });
  if (!data) throw new Error("Empty signed-url response");
  return data;
}

function triggerDownload(url: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
}

/** Stream the file through the backend — avoids presigned-URL signature issues with S3-compatible stores. */
export async function downloadFile(fileId: string): Promise<void> {
  try {
    const { data } = await api.GET("/files/{file_id}/download-token", {
      params: { path: { file_id: fileId } },
    });
    if (!data) throw new Error("Empty token response");
    const url = new URL(`${apiBaseUrl}/files/${encodeURIComponent(fileId)}/download`);
    url.searchParams.set("token", data.token);
    triggerDownload(url.toString());
  } catch (error) {
    toast.error("Could not download", errorMessage(error));
  }
}

/**
 * Download a folder and everything in it as one zip. The server streams it, so the browser saves it straight to
 * disk (it is never held in memory) and the page stays where it is. A download token in the URL lets the
 * browser's download manager resume the transfer without needing the session cookie.
 */
export async function downloadFolder(folderId: string): Promise<void> {
  try {
    const { data } = await api.GET("/folders/{folder_id}/download-token", {
      params: { path: { folder_id: folderId } },
    });
    if (!data) throw new Error("Empty token response");
    const url = new URL(`${apiBaseUrl}/folders/${encodeURIComponent(folderId)}/download`);
    url.searchParams.set("token", data.token);
    triggerDownload(url.toString());
  } catch (error) {
    toast.error("Could not download", errorMessage(error));
  }
}
